/**
 * CampaignsTable Component
 *
 * Campaigns that delivered in the period. Each is judged on the result its
 * goal asks for (leads, purchases, clicks...), so "Results" and "Cost /
 * result" are per campaign, in that campaign's own currency.
 */

import React from 'react';
import type { CampaignSummary } from '@/types/analytics';
import { useTheme } from '@/hooks/useTheme';
import {
  PLATFORM_COLOR,
  costPerLabel,
  formatCount,
  formatMoney,
  formatPct,
  platformLabel,
  resultNoun,
} from '@/utils/analyticsFormat';

export type CampaignSort = 'spend' | 'results' | 'cost_per_result';

export interface CampaignsTableProps {
  campaigns: CampaignSummary[];
  showPlatform: boolean;
  sort: CampaignSort;
  onSort: (sort: CampaignSort) => void;
  onOpen: (campaign: CampaignSummary) => void;
}

const STATUS_STYLE: Record<string, string> = {
  active: 'bg-success-soft text-success',
  paused: 'bg-surface text-ink-2',
  ended: 'bg-surface text-ink-3',
};

const SortHeader: React.FC<{
  label: string; value: CampaignSort; sort: CampaignSort; onSort: (s: CampaignSort) => void;
}> = ({ label, value, sort, onSort }) => (
  <th className="px-4 sm:px-[18px] py-3 text-right" aria-sort={sort === value ? (value === 'cost_per_result' ? 'ascending' : 'descending') : 'none'}>
    <button
      type="button"
      onClick={() => onSort(value)}
      className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider ${sort === value ? 'text-ink' : 'hover:text-ink-2'}`}
    >
      {label}
      {sort === value && (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d={value === 'cost_per_result' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'} />
        </svg>
      )}
    </button>
  </th>
);

export const CampaignsTable: React.FC<CampaignsTableProps> = ({ campaigns, showPlatform, sort, onSort, onOpen }) => {
  const { theme } = useTheme();
  const colors = PLATFORM_COLOR[theme === 'dark' ? 'dark' : 'light'];

  return (
    <div className="overflow-hidden rounded-panel bg-card">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[600px] text-left">
          <thead>
            <tr className="border-b border-border text-[11px] font-bold uppercase tracking-wider text-ink-3">
              <th className="px-4 sm:px-[18px] py-3">Campaign</th>
              {showPlatform && <th className="px-4 sm:px-[18px] py-3">Platform</th>}
              <SortHeader label="Spend" value="spend" sort={sort} onSort={onSort} />
              <SortHeader label="Results" value="results" sort={sort} onSort={onSort} />
              <SortHeader label="Cost / result" value="cost_per_result" sort={sort} onSort={onSort} />
              <th className="px-4 sm:px-[18px] py-3 text-right">Click rate</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map((c) => (
              <tr
                key={`${c.platform}:${c.id}`}
                onClick={() => onOpen(c)}
                onKeyDown={(e) => { if (e.key === 'Enter') onOpen(c); }}
                tabIndex={0}
                className="border-b border-border last:border-b-0 hover:bg-surface/60 cursor-pointer transition-colors tabular-nums"
              >
                <td className="px-4 sm:px-[18px] py-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="text-sm font-semibold text-ink line-clamp-1">{c.name}</span>
                    <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold capitalize ${STATUS_STYLE[c.status] ?? STATUS_STYLE.paused}`}>
                      {c.status}
                    </span>
                  </div>
                </td>
                {showPlatform && (
                  <td className="px-4 sm:px-[18px] py-3">
                    <span className="inline-flex items-center gap-1.5 text-[13px] text-ink-2">
                      <span className="w-2 h-2 rounded-[2px]" style={{ background: colors[c.platform] ?? '#999' }} />
                      {platformLabel(c.platform)}
                    </span>
                  </td>
                )}
                <td className="px-4 sm:px-[18px] py-3 text-right text-[13px] font-semibold text-ink">
                  {formatMoney(c.spend, c.currency)}
                </td>
                <td className="px-4 sm:px-[18px] py-3 text-right text-[13px] text-ink">
                  {formatCount(c.result_count)}{' '}
                  <span className="text-ink-3">{resultNoun(c.result_type, c.result_count)}</span>
                </td>
                <td className="px-4 sm:px-[18px] py-3 text-right text-[13px] text-ink-2" title={costPerLabel(c.result_type)}>
                  {formatMoney(c.cost_per_result, c.currency)}
                  {c.result_type === 'impressions' && c.cost_per_result !== null && (
                    <span className="text-ink-3"> / 1K</span>
                  )}
                </td>
                <td className="px-4 sm:px-[18px] py-3 text-right text-[13px] text-ink-2">{formatPct(c.ctr)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CampaignsTable;
